import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Send, ArrowLeft, Hash, Users, Settings, Search, Plus, UserPlus, Paperclip, Mic, Video, Bot, Crown, Download, FileText, Image, Music, File, Zap, LogOut, User, Building, Loader2, Upload, X } from "lucide-react";
import { HexIcon } from "@/components/ui/hex-icon";
import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { chatService } from "@/lib/chat-service";
import { supabase } from "@/integrations/supabase/client";
import MessageSender from "@/components/MessageSender";
import AddMembersDialog from "@/components/AddMembersDialog";
import { useAuth } from "@/contexts/auth-context";
import { useTenant } from "@/contexts/tenant-context";
import { WorkspaceSettingsModal } from "@/components/WorkspaceSettingsModal";
import { WorkspaceCreationModal } from "@/components/WorkspaceCreationModal";
import { WorkspaceService } from "@/services/workspace-service";
import { ProjectMemberService } from "@/services/project-member-service";
import { getCurrentTenantSlug } from "@/utils/tenant-utils";
import { Header } from '@/components/Header';

const Chat = () => {
  const { user, signOut, loading: authLoading, showWorkspaceModal, closeWorkspaceModal } = useAuth();
  const { tenant } = useTenant();
  const [showWorkspaceSettings, setShowWorkspaceSettings] = useState(false);
  const [currentWorkspace, setCurrentWorkspace] = useState<any>(null);
  
  const [activeChannel, setActiveChannel] = useState<any>(null);
  const [channels, setChannels] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [channelMembers, setChannelMembers] = useState<any[]>([]);
  const [unreadCounts, setUnreadCounts] = useState<{[key: string]: number}>({});
  const [attachmentUrls, setAttachmentUrls] = useState<{[key: string]: string}>({});
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelDescription, setNewChannelDescription] = useState("");
  const [newChannelProject, setNewChannelProject] = useState("");
  const [newChannelProcessArea, setNewChannelProcessArea] = useState("");
  const [newChannelDialogOpen, setNewChannelDialogOpen] = useState(false);
  const [addMembersDialogOpen, setAddMembersDialogOpen] = useState(false);
  const [projects, setProjects] = useState<any[]>([]);
  const [processAreas, setProcessAreas] = useState<any[]>([]);
  const [canCreateChannels, setCanCreateChannels] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load current workspace data for header
  useEffect(() => {
    const loadCurrentWorkspace = async () => {
      if (!user) return;

      try {
        const currentSlug = getCurrentTenantSlug();
        
        if (currentSlug && currentSlug !== 'default') {
          try {
            const workspace = await WorkspaceService.getWorkspaceBySlug(currentSlug);
            setCurrentWorkspace(workspace);
            return;
          } catch (workspaceError) {
            console.warn('⚠️ Chat: Could not load workspace by slug, checking user memberships:', workspaceError);
            
            // Fallback: Check if user has access to any workspace with this slug
            const userWorkspaces = await WorkspaceService.getUserWorkspaces();
            const matchingWorkspace = userWorkspaces.find(ws => ws.slug === currentSlug);
            
            if (matchingWorkspace) {
              setCurrentWorkspace(matchingWorkspace);
              return;
            } else {
              console.log('❌ Chat: User does not have access to workspace:', currentSlug);
            }
          }
        }
        
        // Fallback: Get user's actual workspace instead of creating fake one
        if (user) {
          console.log('🔧 Chat: No workspace loaded, trying to get user default workspace...');
          try {
            const defaultWorkspace = await WorkspaceService.getDefaultWorkspace();
            if (defaultWorkspace) {
              console.log('✅ Chat: Loaded default workspace as fallback:', defaultWorkspace);
              setCurrentWorkspace(defaultWorkspace);
            } else {
              // If no default, get any workspace the user has
              const userWorkspaces = await WorkspaceService.getUserWorkspaces();
              if (userWorkspaces.length > 0) {
                console.log('✅ Chat: Loaded first available workspace as fallback:', userWorkspaces[0]);
                setCurrentWorkspace(userWorkspaces[0]);
              } else {
                console.log('❌ Chat: User has no workspaces - settings will be limited');
                setCurrentWorkspace(null);
              }
            }
          } catch (fallbackError) {
            console.error('❌ Chat: Failed to load fallback workspace:', fallbackError);
            setCurrentWorkspace(null);
          }
        }
      } catch (error) {
        console.error('❌ Chat: Error loading workspace:', error);
        
        // Error fallback
        if (user) {
          console.log('🔧 Chat: Error fallback workspace');
          setCurrentWorkspace({
            id: 'error-fallback',
            name: 'Workspace',
            slug: 'default', 
            domain: null,
            domain_verified: false,
            plan_type: 'free',
            is_default: false
          });
        }
      }
    };

    loadCurrentWorkspace();
  }, [user]);

  // Load initial data
  useEffect(() => {
    const initializeChat = async () => {
      if (initialized) return; // Prevent multiple runs
      try {
        // Get current user
        const user = await chatService.getCurrentUser();
        setCurrentUser(user);

        // Load channels for the current workspace only (service scopes by workspace)
        const channelsData = await chatService.getChannels();
        setChannels(channelsData);

        // Load projects
        await loadProjects();
        await checkCanCreateChannels(); // Check channel creation permissions

        // Set first channel as active if available
        if (channelsData.length > 0) {
          setActiveChannel(channelsData[0]);
        }

        setLoading(false);
        setInitialized(true); // Mark as initialized
      } catch (error) {
        console.error('Error initializing chat:', error);
        setLoading(false);
      }
    };

    initializeChat();
  }, [initialized]); // Add initialized to dependencies

  // Load process areas when project changes
  useEffect(() => {
    if (newChannelProject) {
      loadProcessAreas(newChannelProject);
    } else {
      setProcessAreas([]);
      setNewChannelProcessArea("");
    }
  }, [newChannelProject]);

  // Resolve active workspace id prioritizing subdomain/tenant slug
  const resolveActiveWorkspaceId = async (): Promise<string | undefined> => {
    try {
      const slug = getCurrentTenantSlug();
      if (slug && slug !== 'default') {
        const { data: ws } = await (supabase as any)
          .from('watt_workspaces')
          .select('id')
          .eq('slug', slug)
          .single();
        if (ws?.id) return ws.id as string;
      }
    } catch {}
    // Fallbacks
    if (currentWorkspace?.id) return currentWorkspace.id as string;
    try {
      const ws = await WorkspaceService.getDefaultWorkspace();
      return ws?.id;
    } catch { return undefined; }
  };

  // Load projects that the current user can access, limited to the current workspace
  const loadProjects = async () => {
    try {
      // Get projects the user is a member of
      const accessible = await ProjectMemberService.getUserAccessibleProjects();

      // Determine active workspace id (prefer slug)
      const workspaceId = await resolveActiveWorkspaceId();

      const filtered = (accessible || []).filter((p: any) => !workspaceId || p.workspace_id === workspaceId);
      // Map to fields used by the select
      const list = filtered.map((p: any) => ({ id: p.id, name: p.name, description: p.description }));
      // Sort by name for consistency
      list.sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''));
      setProjects(list);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
    }
  };

  // Check if user can create channels (must be member of at least one project)
  const checkCanCreateChannels = async () => {
    try {
      if (!user) {
        setCanCreateChannels(false);
        return;
      }
      const accessible = await ProjectMemberService.getUserAccessibleProjects();
      const workspaceId = await resolveActiveWorkspaceId();
      const inWorkspace = (accessible || []).filter((p: any) => !workspaceId || p.workspace_id === workspaceId);
      setCanCreateChannels(inWorkspace.length > 0);
    } catch (error) {
      console.error('Error checking channel creation permissions:', error);
      setCanCreateChannels(false);
    }
  };

  // Load process areas for a specific project
  const loadProcessAreas = async (projectId: string) => {
    try {
      const { data, error } = await (supabase as any)
        .from('manufacturing_processes')
        .select('id, name, description, level')
        .eq('product_id', projectId)
        .order('name');

      if (error) throw error;
      setProcessAreas(data || []);
    } catch (error) {
      console.error('Error loading process areas:', error);
      setProcessAreas([]);
    }
  };

  // Load messages when active channel changes
  useEffect(() => {
    const loadChannelData = async () => {
      if (activeChannel) {
        try {
          // Load messages
          const messagesData = await chatService.getMessages(activeChannel.id);
          setMessages(messagesData);

          // Load channel members
          const membersData = await chatService.getChannelMembers(activeChannel.id);
          setChannelMembers(membersData);
        } catch (error) {
          console.error('Error loading channel data:', error);
        }
      }
    };

    loadChannelData();
  }, [activeChannel]);

  // Prefetch signed URLs for image attachments when messages change
  useEffect(() => {
    let cancelled = false;
    const prefetchAttachmentUrls = async () => {
      try {
        const needed: Record<string, true> = {};
        for (const msg of messages) {
          const atts = (msg.attachments || []) as any[];
          for (const att of atts) {
            if (att?.file_type?.startsWith('image/') && att?.storage_path && !attachmentUrls[att.storage_path]) {
              needed[att.storage_path] = true;
            }
          }
        }
        const updates: Record<string, string> = {};
        const paths = Object.keys(needed);
        for (const path of paths) {
          try {
            const url = await chatService.getAttachmentUrl(path);
            updates[path] = url;
          } catch (e) {
            // ignore individual failures
          }
        }
        if (!cancelled && Object.keys(updates).length > 0) {
          setAttachmentUrls(prev => ({ ...prev, ...updates }));
        }
      } catch (e) {
        // ignore batch failure
      }
    };
    prefetchAttachmentUrls();
    return () => { cancelled = true; };
  }, [messages]);

  // Track unread messages
  const markChannelAsRead = (channelId: string) => {
    setUnreadCounts(prev => ({ ...prev, [channelId]: 0 }));
  };

  const incrementUnreadCount = (channelId: string) => {
    if (activeChannel?.id !== channelId) {
      setUnreadCounts(prev => ({ 
        ...prev, 
        [channelId]: (prev[channelId] || 0) + 1 
      }));
    }
  };

  // Mark channel as read when it becomes active
  useEffect(() => {
    if (activeChannel) {
      markChannelAsRead(activeChannel.id);
    }
  }, [activeChannel]);

  // Real-time message subscription
  useEffect(() => {
    let subscription: any = null;

    if (activeChannel) {
      
      subscription = chatService.subscribeToMessages(activeChannel.id, (newMessage: any) => {
        
        // Add the new message to the messages array
        setMessages(prevMessages => {
          
          // Check if message already exists to avoid duplicates
          const messageExists = prevMessages.some(msg => msg.id === newMessage.id);
          if (messageExists) {
            return prevMessages;
          }
          
          return [...prevMessages, newMessage];
        });

        // Increment unread count for other channels
        incrementUnreadCount(activeChannel.id);
      });

    } else {
    }

    // Cleanup subscription when component unmounts or channel changes
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [activeChannel]);

  // Global message subscription for unread counts
  useEffect(() => {
    let subscription: any = null;

    
    subscription = chatService.subscribeToAllMessages(async (messageUpdate: any) => {
      
      if (messageUpdate && messageUpdate.new) {
        const channelId = messageUpdate.new.channel_id;
        const { data: { user } } = await supabase.auth.getUser();
        const currentUserId = user?.id;
        
        // Only increment if the message is not from the current user and not in the active channel
        if (messageUpdate.new.user_id !== currentUserId && activeChannel?.id !== channelId) {
          incrementUnreadCount(channelId);
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [activeChannel]);

  // Real-time channels subscription
  useEffect(() => {
    let subscription: any = null;

    
    subscription = chatService.subscribeToChannels(async (newChannel: any) => {
      
      if (newChannel) {
        // Get current user to check if they're the creator
        const { data: { user } } = await supabase.auth.getUser();
        const isCreator = user && newChannel.created_by === user.id;
        
        
        // If user is the creator, add immediately (they'll be added as member automatically)
        if (isCreator) {
          setChannels(prevChannels => {
            // Check if channel already exists by ID
            const channelExists = prevChannels.some(ch => ch.id === newChannel.id);
            
            if (channelExists) {
              return prevChannels.map(ch => ch.id === newChannel.id ? newChannel : ch);
            } else {
              return [...prevChannels, newChannel];
            }
          });
        } else {
          // For non-creators, wait a moment for membership to be established, then check
          setTimeout(async () => {
            try {
              
              const isMember = await chatService.isUserChannelMember(newChannel.id);
              
              if (isMember) {
                setChannels(prevChannels => {
                  // Check if channel already exists by ID
                  const channelExists = prevChannels.some(ch => ch.id === newChannel.id);
                  if (channelExists) {
                    return prevChannels.map(ch => ch.id === newChannel.id ? newChannel : ch);
                  } else {
                    return [...prevChannels, newChannel];
                  }
                });
              } else {
                console.log(`📋 User is NOT a member of channel ${newChannel.id}, NOT adding to sidebar`);
                console.log(`📋 Channel name: ${newChannel.name}`);
                console.log(`📋 Channel created by: ${newChannel.created_by}`);
              }
            } catch (error) {
              console.error('Error checking channel membership:', error);
            }
          }, 1000); // Wait 1 second for membership to be established
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  // Real-time channel members subscription
  useEffect(() => {
    let subscription: any = null;

    if (activeChannel) {
      
      subscription = chatService.subscribeToChannelMembers(activeChannel.id, (memberUpdate: any) => {
        
        // Refresh channel members when there's an update
        const refreshMembers = async () => {
          try {
            const members = await chatService.getChannelMembers(activeChannel.id);
            setChannelMembers(members);
          } catch (error) {
            console.error('Error refreshing channel members:', error);
          }
        };
        
        refreshMembers();
      });

    }

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [activeChannel]);

  // Real-time watt_members subscription for channel list updates
  useEffect(() => {
    let subscription: any = null;

    
    subscription = chatService.subscribeToWattMembers((memberUpdate: any) => {
      
      if (memberUpdate && memberUpdate.new) {
        // If this user was added to a channel, refresh the channel list
        if (memberUpdate.new.user_id === currentUser?.id) {
          console.log('👥 User was added to a channel, refreshing channel list');
          console.log('👥 Member update details:', {
            user_id: memberUpdate.new.user_id,
            current_user_id: currentUser?.id,
            channel_id: memberUpdate.new.channel_id,
            event_type: memberUpdate.eventType
          });
          
          const refreshChannels = async () => {
            try {
              const channelsData = await chatService.getChannels();
              setChannels(channelsData);
            } catch (error) {
              console.error('Error refreshing channels:', error);
            }
          };
          refreshChannels();
        }
      }
    });

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [currentUser]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({ 
          behavior: "smooth",
          block: "end",
          inline: "nearest"
        });
      }
    };

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(scrollToBottom, 100);
    return () => clearTimeout(timeoutId);
  }, [messages]);

  const handleCreateChannel = async () => {
    if (newChannelName.trim()) {
      try {
        const channelData = {
          name: newChannelName.trim(),
          description: newChannelDescription.trim(),
          project_id: newChannelProject,
          process_area_id: newChannelProcessArea || null,
          created_by: currentUser?.id
        };
        const newChannel = await chatService.createChannel(channelData);
        // Do not optimistically add; realtime subscription will add it
        setActiveChannel(newChannel);
        setNewChannelName("");
        setNewChannelDescription("");
        setNewChannelProject("");
        setNewChannelProcessArea("");
        setNewChannelDialogOpen(false);
      } catch (error) {
        console.error('Error creating channel:', error);
      }
    }
  };

  const handleMessageSent = (newMessage: any) => {
    setMessages(prev => [...prev, newMessage]);
  };

  const handleAddMember = async (member: any) => {
    if (activeChannel) {
      try {
        await chatService.addChannelMember(activeChannel.id, member.id);
        // Reload channel members
        const membersData = await chatService.getChannelMembers(activeChannel.id);
        setChannelMembers(membersData);
      } catch (error) {
        console.error('Error adding member:', error);
      }
    }
  };

  const formatTime = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatFileSize = (size: number) => {
    if (size < 1024) {
      return size + ' B';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(2) + ' KB';
    } else if (size < 1024 * 1024 * 1024) {
      return (size / (1024 * 1024)).toFixed(2) + ' MB';
    } else {
      return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    }
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.startsWith('image/')) return <Image className="w-4 h-4" />
    if (fileType.startsWith('video/')) return <Video className="w-4 h-4" />
    if (fileType.startsWith('audio/')) return <Music className="w-4 h-4" />
    if (fileType.includes('pdf')) return <FileText className="w-4 h-4" />
    return <File className="w-4 h-4" />
  };

  const handleDownloadAttachment = async (attachment: any) => {
    try {
      // Private bucket: use signed URL for download (short-lived)
      const downloadUrl = await chatService.getAttachmentUrl(attachment.storage_path);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading chat...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      {/* Header */}
      <Header onSettingsClick={() => setShowWorkspaceSettings(true)} />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar */}
        <div className="w-80 border-r bg-card/30 flex flex-col">
          {/* Quality Channels Section */}
          <div className="flex flex-col flex-1 min-h-0">
            {/* Channels Header */}
            <div className="p-4 border-b flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-foreground">Watt Channels</h2>
                <Dialog open={newChannelDialogOpen} onOpenChange={setNewChannelDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 hover:bg-primary/10"
                      disabled={!canCreateChannels}
                      title={canCreateChannels ? "Create new channel" : "You must be a project member to create channels"}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create New Watt Channel</DialogTitle>
                      <DialogDescription>
                        Create a new watt channel to track and manage specific manufacturing issues or processes.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-6">
                      {/* Title */}
                      <div className="space-y-2">
                        <Label htmlFor="title">Title</Label>
                        <Input
                          id="title"
                          value={newChannelName}
                          onChange={(e) => setNewChannelName(e.target.value)}
                          placeholder="Enter channel title"
                        />
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <textarea
                          id="description"
                          value={newChannelDescription}
                          onChange={(e) => setNewChannelDescription(e.target.value)}
                          className="w-full min-h-[100px] p-3 border border-input rounded-md bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          placeholder="Describe the quality issue or process"
                        />
                      </div>

                      {/* Project */}
                      <div className="space-y-2">
                        <Label htmlFor="project">Project</Label>
                        <select
                          id="project"
                          value={newChannelProject}
                          onChange={(e) => setNewChannelProject(e.target.value)}
                          className="w-full p-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        >
                          <option value="">Select project</option>
                          {projects.map((project) => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Process Area (Optional) */}
                      {newChannelProject && (
                        <div className="space-y-2">
                          <Label htmlFor="processArea">Process Area (Optional)</Label>
                          <select
                            id="processArea"
                            value={newChannelProcessArea}
                            onChange={(e) => setNewChannelProcessArea(e.target.value)}
                            className="w-full p-3 border border-input rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          >
                            <option value="">Select process area (optional)</option>
                            {processAreas.map((area) => (
                              <option key={area.id} value={area.id}>
                                {area.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex gap-3 pt-4">
                        <Button 
                          variant="outline" 
                          className="flex-1"
                          onClick={() => setNewChannelDialogOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button 
                          className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
                          onClick={handleCreateChannel}
                          disabled={!newChannelName.trim() || !newChannelDescription.trim() || !newChannelProject}
                        >
                          Create Channel
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
            
            {/* Channels List - Scrollable */}
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="p-4 space-y-2">
                  {channels.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Hash className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No channels yet</p>
                      {canCreateChannels ? (
                        <p className="text-xs">Create your first watt channel</p>
                      ) : (
                        <p className="text-xs">You need to be a project member to create channels</p>
                      )}
                    </div>
                  ) : (
                    channels.map((channel) => (
                      <button
                        key={channel.id}
                        onClick={() => setActiveChannel(channel)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          activeChannel?.id === channel.id
                            ? "bg-primary/10 border border-primary/20"
                            : "hover:bg-muted/50"
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <Hash className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-medium text-foreground truncate">
                              {channel.code || channel.name}
                            </span>
                          </div>
                          <Badge 
                            variant="secondary" 
                            className={`text-xs ${
                              (unreadCounts[channel.id] || 0) > 0 
                                ? "bg-black text-white hover:bg-black" 
                                : ""
                            }`}
                          >
                            {unreadCounts[channel.id] || 0}
                          </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {channel.name}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>

          {/* Channel Members Section */}
          <div className="flex flex-col flex-1 min-h-0 border-t">
            {/* Members Header */}
            <div className="p-4 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <h3 className="font-medium text-foreground">Channel Members</h3>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-8 w-8 p-0 hover:bg-primary/10"
                  title="Add member to channel"
                  disabled={!activeChannel}
                  onClick={() => setAddMembersDialogOpen(true)}
                >
                  <UserPlus className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Members List - Scrollable */}
            <div className="flex-1 overflow-hidden">
              <ScrollArea className="h-full">
                <div className="px-4 pb-4 space-y-2">
                  {channelMembers.length === 0 ? (
                    <div className="text-center py-4 text-muted-foreground">
                      <Users className="w-6 h-6 mx-auto mb-2 opacity-50" />
                      <p className="text-xs">No members yet</p>
                    </div>
                  ) : (
                    channelMembers.map((member, index) => (
                      <div key={index} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30">
                        <Avatar className="w-8 h-8">
                          <AvatarImage />
                          <AvatarFallback className="bg-secondary text-secondary-foreground text-xs">
                            {member.user?.display_name?.split(' ').map((n: string) => n[0]).join('') || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-foreground truncate">
                              {member.user?.display_name || 'Unknown User'}
                            </p>
                            {/* Show crown icon if this member is the problem owner */}
                            {activeChannel?.problem_owner === member.user?.id && (
                              <div title="Problem Owner">
                                <Crown className="w-3 h-3 text-yellow-500 flex-shrink-0" />
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate">
                            {member.user?.role || 'Member'}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          </div>
        </div>

        {/* Main Chat Area */}
        <div className="flex-1 flex flex-col">
          {activeChannel ? (
            <>
              {/* Header */}
              <div className="border-b bg-card/50 backdrop-blur-sm flex-shrink-0">
                <div className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Link to={`/${window.location.search}`}>
                        <Button variant="ghost" size="sm" className="gap-2">
                          <ArrowLeft className="w-4 h-4" />
                          Back
                        </Button>
                      </Link>
                      <div className="flex items-center gap-2">
                        <Hash className="w-5 h-5 text-muted-foreground" />
                        <h1 className="text-xl font-semibold text-foreground">
                          {activeChannel.code || activeChannel.name}
                        </h1>
                        <Badge variant="secondary" className="ml-2">
                          {channelMembers.length} members
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" title="Coming Soon" disabled>
                        <Mic className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Coming Soon" disabled>
                        <Video className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" title="Coming Soon" disabled>
                        <Search className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 ml-16">
                    {activeChannel.name}
                  </p>
                </div>
              </div>

              {/* Chat Messages - Scrollable Area */}
              <div className="flex-1 overflow-hidden min-h-0">
                <ScrollArea className="h-full">
                  <div className="px-4 py-4 space-y-4 pb-12">
                    {messages.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <Hash className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <h3 className="text-lg font-medium mb-2">Welcome to #{activeChannel.code || activeChannel.name}</h3>
                        <p className="text-sm">This is the beginning of your conversation.</p>
                        <p className="text-sm">Send a message to get started!</p>
                      </div>
                    ) : (
                      messages.map((message) => {
                        const isCurrentUser = message.user?.id === currentUser?.id
                        
                        return (
                          <div key={message.id} className={`flex gap-3 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                            {/* Avatar for incoming messages (left side) */}
                            {!isCurrentUser && (
                              <Avatar className="w-10 h-10 mt-1">
                                <AvatarImage />
                                <AvatarFallback className={`text-xs ${
                                  message.user?.role === 'AI Assistant'
                                    ? "bg-primary text-primary-foreground" 
                                    : "bg-secondary text-secondary-foreground"
                                }`}>
                                  {message.user?.display_name?.split(' ').map((n: string) => n[0]).join('') || '?'}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            
                            <div className={`flex-1 min-w-0 ${isCurrentUser ? 'flex flex-col items-end' : ''}`}>
                              {/* Message header */}
                              <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}>
                                <span className="text-sm font-medium text-foreground">
                                  {message.user?.display_name || 'Unknown User'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {formatTime(message.created_at)}
                                </span>
                              </div>
                              
                              {/* Message bubble */}
                              <div className={`p-3 rounded-lg max-w-md ${isCurrentUser 
                                ? 'bg-primary text-primary-foreground' 
                                : 'bg-muted text-foreground w-fit'
                              }`}>
                                <div className="text-sm leading-relaxed whitespace-pre-line">
                                  {message.content}
                                </div>
                                
                                {/* Display attachments if any */}
                                {message.attachments && message.attachments.length > 0 && (
                                  <div className="mt-3 space-y-3">
                                    {message.attachments.map((attachment: any, index: number) => {
                                      const isImage = attachment.file_type.startsWith('image/')
                                      
                                      return (
                                        <div key={index} className={`rounded-lg overflow-hidden ${
                                          isCurrentUser 
                                            ? 'bg-primary-foreground/10 border border-primary-foreground/20' 
                                            : 'bg-background border border-border'
                                        }`}>
                                          {/* Image preview */}
                                          {isImage && (
                                            <div className="relative">
                                              {attachmentUrls[attachment.storage_path] ? (
                                                <img 
                                                  src={attachmentUrls[attachment.storage_path]}
                                                  alt={attachment.file_name}
                                                  className="w-full max-w-sm h-auto rounded-t-lg cursor-pointer hover:opacity-90 transition-opacity"
                                                  onClick={() => handleDownloadAttachment(attachment)}
                                                />
                                              ) : (
                                                <div className="w-full max-w-sm h-40 bg-muted animate-pulse" />
                                              )}
                                              <div className="absolute top-2 right-2">
                                                <Button
                                                  variant="secondary"
                                                  size="sm"
                                                  className="h-8 w-8 p-0 bg-black/50 hover:bg-black/70 text-white border-0"
                                                  onClick={() => handleDownloadAttachment(attachment)}
                                                  title="Download"
                                                >
                                                  <Download className="w-3 h-3" />
                                                </Button>
                                              </div>
                                            </div>
                                          )}
                                          
                                          {/* File info */}
                                          <div className="p-3">
                                            <div className="flex items-center gap-2">
                                              <div className={`${
                                                isCurrentUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                              }`}>
                                                {getFileIcon(attachment.file_type)}
                                              </div>
                                              <div className="flex-1 min-w-0">
                                                <p className={`text-sm font-medium truncate ${
                                                  isCurrentUser ? 'text-primary-foreground' : 'text-foreground'
                                                }`}>
                                                  {attachment.file_name}
                                                </p>
                                                <p className={`text-xs ${
                                                  isCurrentUser ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                                }`}>
                                                  {attachment.file_type} • {formatFileSize(attachment.file_size)}
                                                </p>
                                              </div>
                                              {!isImage && (
                                                <Button
                                                  variant="ghost"
                                                  size="sm"
                                                  className={`h-8 w-8 p-0 ${
                                                    isCurrentUser 
                                                      ? 'hover:bg-primary-foreground/10 text-primary-foreground/70 hover:text-primary-foreground' 
                                                      : 'hover:bg-muted text-muted-foreground hover:text-foreground'
                                                  }`}
                                                  onClick={() => handleDownloadAttachment(attachment)}
                                                  title="Download"
                                                >
                                                  <Download className="w-4 h-4" />
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Avatar for current user messages (right side) */}
                            {isCurrentUser && (
                              <Avatar className="w-10 h-10 mt-1">
                                <AvatarImage />
                                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                                  {message.user?.display_name?.split(' ').map((n: string) => n[0]).join('') || '?'}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                        )
                      })
                    )}
                    {/* Invisible element to scroll to */}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
              </div>

              {/* Message Input - Fixed at Bottom */}
              <div className="flex-shrink-0">
                {currentUser && (
                  <MessageSender
                    channelId={activeChannel.id}
                    currentUser={currentUser}
                    onMessageSent={handleMessageSent}
                  />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <Hash className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Channel Selected</h3>
                <p className="text-sm">Select a channel from the sidebar to start chatting</p>
              </div>
            </div>
          )}
        </div>

        {/* Add Members Dialog */}
        <AddMembersDialog
          open={addMembersDialogOpen}
          onOpenChange={setAddMembersDialogOpen}
          currentMembers={channelMembers}
          onAddMember={handleAddMember}
          projectId={activeChannel?.project_id}
        />
      </div>

      {/* Workspace Settings Modal */}
      {currentWorkspace && (
        <WorkspaceSettingsModal
          isOpen={showWorkspaceSettings}
          onClose={() => setShowWorkspaceSettings(false)}
          workspace={{
            id: currentWorkspace.id,
            name: currentWorkspace.name,
            slug: currentWorkspace.slug,
            plan_type: currentWorkspace.plan_type || 'free',
            domain: currentWorkspace.domain || null,
            domain_verified: currentWorkspace.domain_verified || false,
            is_default: currentWorkspace.is_default || false
          }}
          onWorkspaceUpdate={(updatedWorkspace) => {
            setCurrentWorkspace({ ...currentWorkspace, ...updatedWorkspace });
          }}
        />
      )}

      {/* Workspace Creation Modal */}
      <WorkspaceCreationModal
        isOpen={showWorkspaceModal}
        onClose={closeWorkspaceModal}
        userEmail={user?.email || ''}
        userName={(user as any)?.user_metadata?.full_name || (user as any)?.user_metadata?.name || user?.email || 'User'}
      />
    </div>
  );
};

export default Chat;